import React, { useState, useEffect } from 'react';
import {
  IconPlus,
  IconLoader2
} from '@tabler/icons-react';

const ServersTab = () => {
  const [tunnels, setTunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null);

  // Загрузить туннели при монтировании и обновлять каждые 5 сек
  useEffect(() => {
    const fetchTunnels = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
        const token = localStorage.getItem('access_token');
        
        const response = await fetch(`${API_BASE_URL}/v1/tunnels`, {
          headers: {
            'Authorization': `Bearer ${token || ''}`
          },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setTunnels(data || []);
        }
      } catch (error) {
        console.log('Ошибка загрузки туннелей:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTunnels();
    const interval = setInterval(fetchTunnels, 5000);
    return () => clearInterval(interval);
  }, []);

  // Группировать туннели по типам игр
  const gameTypes = [
    { id: 'minecraft', name: 'Minecraft', color: 'bg-green-500' },
    { id: 'cs2', name: 'Counter-Strike 2', color: 'bg-blue-500' },
    { id: 'terraria', name: 'Terraria', color: 'bg-purple-500' },
    { id: 'other', name: 'Другое', color: 'bg-orange-500' },
  ];

  const getTunnelsByType = (gameId) => {
    return tunnels.filter(t => {
      const tunnelGame = (t.game || 'other').toLowerCase();
      // Маппируем игры - учитываем оба варианта Minecraft
      if (gameId === 'minecraft') return tunnelGame.startsWith('minecraft') || tunnelGame === 'minecraft-java' || tunnelGame === 'minecraft-bedrock';
      if (gameId === 'cs2') return tunnelGame === 'cs2' || tunnelGame === 'counter-strike' || tunnelGame === 'counter-strike-2';
      if (gameId === 'terraria') return tunnelGame === 'terraria';
      if (gameId === 'other') return !['minecraft', 'minecraft-java', 'minecraft-bedrock', 'cs2', 'counter-strike', 'counter-strike-2', 'terraria'].includes(tunnelGame);
      return false;
    });
  };

  const getTunnelColor = (gameType) => {
    const game = gameTypes.find(g => g.id === gameType);
    return game?.color || 'bg-orange-500';
  };

  const getStatusDisplay = (tunnel) => {
    // Определяем статус и цвет
    if (tunnel.status === 'active') return { text: 'Активен', color: 'text-green-400' };
    if (tunnel.status === 'waiting_for_client') return { text: 'Ожидание клиента', color: 'text-yellow-400' };
    if (tunnel.status === 'inactive') return { text: 'Неактивен', color: 'text-red-400' };
    return { text: tunnel.status || 'Подключение', color: 'text-dark-400' };
  };

  const getPublicAddress = (tunnel) => {
    // Формируем публичный адрес из host и port
    if (tunnel.public_address) return tunnel.public_address;
    if (tunnel.publicAddress) return tunnel.publicAddress;
    
    const host = tunnel.public_host || tunnel.publicHost;
    const port = tunnel.public_port || tunnel.publicPort;
    
    if (host && port) return `${host}:${port}`;
    if (host) return host;
    
    return 'подключение...';
  };

  return (
    <div className="space-y-4">
      {gameTypes.map((game) => {
        const gameTunnels = getTunnelsByType(game.id);
        const isExpanded = activeGame === game.id;

        return (
          <div key={game.id} className="bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-xl overflow-hidden">
            {/* Заголовок типа игры */}
            <button
              onClick={() => setActiveGame(isExpanded ? null : game.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-dark-900 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${game.color}`} />
                <span className="text-gray-900 dark:text-white font-medium">{game.name}</span>
                <span className="text-gray-600 dark:text-dark-500 text-sm">({gameTunnels.length})</span>
              </div>
              <div className="text-gray-500 dark:text-dark-600">
                {isExpanded ? '−' : '+'}
              </div>
            </button>

            {/* Список туннелей */}
            {isExpanded && (
              <div className="border-t border-gray-300 dark:border-dark-800 p-3 space-y-2">
                {gameTunnels.length > 0 ? (
                  gameTunnels.map((tunnel) => {
                    const statusDisplay = getStatusDisplay(tunnel);
                    const isDot = tunnel.status === 'active' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 
                                 tunnel.status === 'waiting_for_client' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.4)]' :
                                 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]';
                    
                    return (
                      <div
                        key={tunnel.id}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-900 border border-gray-300 dark:border-dark-800 rounded-lg hover:border-gray-400 dark:hover:border-dark-700 transition-colors"
                      >
                        <div className={`w-2.5 h-2.5 rounded-full ${isDot} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900 dark:text-white text-sm font-semibold truncate">{tunnel.name}</div>
                          <div className="text-gray-600 dark:text-dark-600 text-xs">{getPublicAddress(tunnel)}</div>
                        </div>
                        <div className={`${statusDisplay.color} text-xs font-mono whitespace-nowrap`}>
                          {statusDisplay.text}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-3 text-gray-600 dark:text-dark-600 text-sm">
                    Нет туннелей
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Кнопка создания */}
      <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500/10 border border-primary-500/30 rounded-lg text-primary-500 hover:bg-primary-500/20 hover:border-primary-500/50 transition-colors">
        <IconPlus className="w-4 h-4" />
        <span>Создать новый туннель</span>
      </button>
    </div>
  );
};

export default ServersTab;
